import React from 'react';
import { oneOf } from 'prop-types';
import styled, { css } from 'styled-components';
import { XsText, SmText, Bold } from 'TMComponents/Typography/styles';
import {
    everydayGradient,
    smartGradient,
    smartPlusGradient
} from 'TMComponents/GlobalStyle/cardBackgroundStyles';

const standard = css`
    background-color: ${({ theme }) => theme.colorSystem.secondary[6]};
`;

const popular = css`
    background-color: ${({ theme }) => theme.colorSystem.secondary[7]};
`;

const premium = css`
    background-color: ${({ theme }) => theme.colorSystem.secondary[8]};
`;

const asSeenOnTVDark = css`
    ${({ theme }) =>
        `
         background: linear-gradient(180deg, ${theme.colorSystem.gradients[9][0]}, ${theme.colorSystem.gradients[9][1]} );
        `};
    padding: 8px 6px;
    border-radius: 8px;
`;

const asSeenOnTVLight = css`
    ${({ theme }) =>
        `
        background: linear-gradient(180deg, ${theme.colorSystem.gradients[10][0]}, ${theme.colorSystem.gradients[10][1]} );
        color: ${theme.colorSystem.primary[1]};
        `};
    padding: 8px 6px;
    border-radius: 8px;
`;

const poweredByAI = css`
    padding-left: 7px 11px;
    width: 7.5rem;
    border-radius: 30px;
    background: linear-gradient(
        216deg,
        #e9faff 21.22%,
        #d4efff 32.33%,
        #f7e1f5 51.08%,
        #e9faff 93.44%
    );
`;

const poweredByAILabel = css`
    ${SmText}
    margin:0;
    color: ${({ theme }) => theme.colorSystem.UIText[1]};
    text-transform: none;
    font-size: 0.833rem;
    strong {
        font-weight: 900;
    }
`;

export const pillVariantOptions = [
    'standard',
    'popular',
    'premium',
    'standard_gradient',
    'popular_gradient',
    'premium_gradient',
    'seen_on_tv_dark',
    'seen_on_tv_light',
    'ai',
    'press_release'
];

const variantMap = {
    standard: { label: 'Standard', containerStyles: standard },
    popular: { label: 'Popular', containerStyles: popular },
    premium: { label: 'Premium', containerStyles: premium },
    standard_gradient: {
        label: 'Standard',
        containerStyles: everydayGradient
    },
    popular_gradient: { label: 'Popular', containerStyles: smartGradient },
    premium_gradient: {
        label: 'Premium',
        containerStyles: smartPlusGradient
    },
    seen_on_tv_dark: {
        label: 'As seen on TV',
        containerStyles: asSeenOnTVDark
    },
    seen_on_tv_light: {
        label: 'As seen on TV',
        containerStyles: asSeenOnTVLight
    },
    ai: {
        label: (
            <>
                Powered by <strong>AI</strong>
            </>
        ),
        containerStyles: poweredByAI,
        labelStyles: poweredByAILabel
    },
    press_release: {
        label: 'Press Release',
        containerStyles: smartGradient
    }
};

const smallContainerStyles = css`
    padding: 4px 2px;
    width: 5rem;
`;

const Background = styled.div`
    color: ${({ theme }) => theme.colorSystem.primary[3]};
    box-sizing: border-box;
    padding: 8px 16px;
    width: ${({ variant }) =>
        variant === 'press_release'
            ? '8rem'
            : '6.25rem'}; //100px ensures text isnt overflowing when font is increased whilst keeping width of pills uniform
    border-radius: 12px;
    ${({ size }) => size === 'small' && smallContainerStyles};
    ${({ variant }) => variantMap[variant].containerStyles};
`;

const Label = styled.p`
    ${XsText}
    ${Bold}
    text-align:center;
    text-transform: uppercase;
    margin: 0;

    ${({ size }) => size === 'small' && 'font-size: 0.625rem'};
    ${({ variant }) => variantMap[variant]?.labelStyles};
`;

const Pill = ({ variant = 'standard', size = 'medium' }) => {
    return (
        <Background variant={variant} size={size}>
            <Label variant={variant} size={size}>
                {variantMap[variant].label}
            </Label>
        </Background>
    );
};

export default Pill;

Pill.propTypes = {
    variant: oneOf(pillVariantOptions),
    size: oneOf(['medium', 'small'])
};
